/**
 * "Crew" card on the pickup detail page (workstream C): who's on the run + the
 * Assign-crew picker (supervisors/admins). The pickup twin of ProjectCrewCard;
 * a pickup has one block per crew (no per-day list) and no required lead.
 */
import { Star } from 'lucide-react';
import { usePickupAssignments } from '@/api/pickups';
import { Avatar } from '@/components/ui/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AssignPickupCrewButton } from './AssignPickupCrewButton';

export function PickupCrewCard({
  pickup,
  canManage,
  readOnlyReason,
}: {
  pickup: { id: string; title: string; scheduled_start: string | null };
  canManage: boolean;
  /** When set (complete / cancelled), the picker is hidden even for managers. */
  readOnlyReason?: string | null;
}) {
  const { data, isLoading } = usePickupAssignments(pickup.id);
  const crew = data?.assignments ?? [];
  const showPicker = canManage && !readOnlyReason;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Crew</CardTitle>
        {showPicker && <AssignPickupCrewButton pickup={pickup} />}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-xs text-ink-faint">Loading crew…</div>
        ) : crew.length === 0 ? (
          <div className="text-sm text-ink-dim">
            No crew on this run yet.
            {showPicker && ' Assign crew to put it on their Today.'}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {crew.map((c) => (
              <li key={c.crew_member_id} className="flex items-center gap-2 min-w-0">
                <Avatar name={c.display_name} src={c.avatar_url} className="w-7 h-7 text-[11px]" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate flex items-center gap-1">
                    <span className="truncate">{c.display_name}</span>
                    {c.is_lead && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[10px] text-amber-700 font-medium shrink-0"
                        title="Crew lead"
                      >
                        <Star className="w-3 h-3 fill-amber-400" /> lead
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-faint truncate">{c.crew_role}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
